import React, { useEffect, useState, useCallback } from 'react';
import { CreditCard, Banknote, Receipt, Trash2, Wallet, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * PartialPaymentsPanel
 * ====================
 * Reusable manager-side widget for splitting a tab/order across multiple
 * payment methods. Mounted inside the OrdersPage Settle dialog AND on the
 * direct-order card.
 *
 * Behaviour:
 *  - On mount + after every successful partial → fetch /payments/partial/balance
 *    and re-render.
 *  - Three pill buttons (Cash / Card / EFTPOS) — clicking one toggles an
 *    inline amount input that defaults to the remaining balance. Pressing
 *    Record posts /payments/partial.
 *  - Recorded partials list with a per-row void button (manager/director).
 *  - When remaining ≤ $0.005 → Finalize button appears + onSettled() fires
 *    once it succeeds so the parent can refresh and close.
 *  - Cash 10% Off discount auto-applies/forfeits per backend rules — the
 *    UI just shows what the server returns.
 */
const METHODS = [
  { key: 'cash',   label: 'Cash',   icon: Banknote,   styles: 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/25' },
  { key: 'card',   label: 'Card',   icon: CreditCard, styles: 'bg-blue-accent/15 border-blue-accent/50 text-blue-accent hover:bg-blue-accent/25' },
  { key: 'eftpos', label: 'EFTPOS', icon: Wallet,     styles: 'bg-violet-500/15 border-violet-500/50 text-violet-200 hover:bg-violet-500/25' },
];

export default function PartialPaymentsPanel({ targetType, targetId, onSettled, actorRole }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [openMethod, setOpenMethod] = useState(null);   // key of active input
  const [amountDraft, setAmountDraft] = useState('');
  const [error, setError] = useState(null);

  const canVoid = ['manager', 'director', 'admin', 'owner'].includes(actorRole);

  const refresh = useCallback(async () => {
    if (!targetType || !targetId) return;
    try {
      setLoading(true);
      const { data } = await api.get('/payments/partial/balance', {
        params: { target_type: targetType, target_id: targetId },
      });
      setSummary(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load balance');
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId]);

  useEffect(() => { refresh(); }, [refresh]);

  const recordPartial = async (method) => {
    if (!summary) return;
    const amt = parseFloat(amountDraft);
    if (!amt || amt <= 0) { toast.error('Enter an amount greater than $0'); return; }
    if (amt > summary.remaining + 0.01) {
      toast.error(`Amount $${amt.toFixed(2)} exceeds remaining $${summary.remaining.toFixed(2)}`);
      return;
    }
    try {
      setWorking(true);
      await api.post('/payments/partial', {
        target_type: targetType, target_id: targetId,
        method, amount: amt,
      });
      setAmountDraft('');
      setOpenMethod(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record payment');
    } finally {
      setWorking(false);
    }
  };

  const voidPartial = async (pid) => {
    const reason = window.prompt('Void reason (audit log):', 'Manager void');
    if (reason === null) return;
    try {
      setWorking(true);
      await api.post(`/payments/partial/${pid}/void`, { reason: reason.trim() });
      toast.success('Partial voided');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    } finally {
      setWorking(false);
    }
  };

  const finalize = async () => {
    try {
      setWorking(true);
      await api.post('/payments/partial/finalize', {
        target_type: targetType, target_id: targetId,
      });
      toast.success(targetType === 'tab' ? 'Tab closed' : 'Order paid');
      if (onSettled) onSettled();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Finalize failed');
      setWorking(false);
    }
  };

  if (loading && !summary) {
    return <div className="flex items-center justify-center py-6" data-testid="partials-loading">
      <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>;
  }
  if (error) {
    return <p className="text-xs text-red-300 font-sans bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="partials-error">{error}</p>;
  }
  if (!summary) return null;

  const partials = summary.partials || [];
  const remaining = Number(summary.remaining || 0);

  return (
    <div className="space-y-3" data-testid="partial-payments-panel">
      {/* Live balance card */}
      <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3 space-y-1 font-sans text-sm">
        <div className="flex justify-between text-zinc-400">
          <span>Bill total</span>
          <span className="font-mono text-white" data-testid="partial-sum-total">${Number(summary.sum_total).toFixed(2)}</span>
        </div>
        {Number(summary.upfront_amount) > 0 && (
          <div className="flex justify-between text-zinc-400 text-xs">
            <span>Less deposit credit</span>
            <span className="text-emerald-400 font-mono">−${Number(summary.upfront_amount).toFixed(2)}</span>
          </div>
        )}
        {Number(summary.discount_amount) > 0 && (
          <div className="flex justify-between text-emerald-300 text-xs font-semibold">
            <span>Cash · 10% Off</span>
            <span className="font-mono">−${Number(summary.discount_amount).toFixed(2)}</span>
          </div>
        )}
        {Number(summary.partials_paid) > 0 && (
          <div className="flex justify-between text-zinc-400 text-xs">
            <span>Already collected ({partials.length})</span>
            <span className="text-emerald-400 font-mono">−${Number(summary.partials_paid).toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between pt-1.5 mt-1.5 border-t border-white/10">
          <span className="text-white font-medium uppercase tracking-[0.05em] text-xs">Remaining</span>
          <span className={`font-mono font-bold text-lg ${remaining <= 0.005 ? 'text-emerald-300' : 'text-gold'}`} data-testid="partial-remaining">
            ${remaining.toFixed(2)}
          </span>
        </div>
        {summary.discount_eligible_all_cash && remaining > 0 && (
          <p className="text-[10px] text-emerald-300/80 font-sans italic pt-0.5">
            Stays eligible for the 10% cash discount while every partial is cash.
          </p>
        )}
        {!summary.discount_eligible_all_cash && partials.some(p => p.method === 'card' || p.method === 'eftpos') && Number(summary.sum_total) > 0 && (
          <p className="text-[10px] text-amber-300/80 font-sans italic pt-0.5">
            Cash 10% discount forfeit (a non-cash partial was recorded).
          </p>
        )}
      </div>

      {/* Partial entry buttons */}
      {remaining > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans font-bold">Take a Partial Payment</p>
          <div className="grid grid-cols-3 gap-1.5">
            {METHODS.map(m => {
              const Icon = m.icon;
              return (
                <Button
                  key={m.key}
                  type="button"
                  onClick={() => {
                    if (openMethod === m.key) {
                      setOpenMethod(null);
                      setAmountDraft('');
                    } else {
                      setOpenMethod(m.key);
                      setAmountDraft(remaining.toFixed(2));
                    }
                  }}
                  className={`h-10 rounded-sm border font-sans text-xs uppercase tracking-[0.06em] font-semibold ${openMethod === m.key ? 'bg-gold text-black border-gold' : m.styles}`}
                  data-testid={`partial-method-${m.key}`}
                >
                  <Icon className="w-3.5 h-3.5 mr-1" />
                  {m.label}
                </Button>
              );
            })}
          </div>
          {openMethod && (
            <div className="flex gap-1.5 pt-1" data-testid={`partial-amount-input-${openMethod}`}>
              <span className="self-center text-zinc-500 font-sans text-sm pl-1">$</span>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={(remaining + 0.01).toFixed(2)}
                value={amountDraft}
                onChange={e => setAmountDraft(e.target.value)}
                className="flex-1 h-10 bg-transparent border border-white/15 focus:border-gold text-white font-mono text-base"
                placeholder="0.00"
                autoFocus
                data-testid={`partial-amount-${openMethod}`}
              />
              <Button
                onClick={() => recordPartial(openMethod)}
                disabled={working}
                className="h-10 bg-gold text-black hover:bg-gold-light rounded-sm font-sans font-bold uppercase tracking-[0.06em] text-xs px-4 disabled:opacity-50"
                data-testid={`record-partial-${openMethod}`}
              >
                {working ? '…' : 'Record'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Recorded partials list */}
      {partials.length > 0 && (
        <div className="space-y-1" data-testid="partials-list">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans font-bold">Recorded ({partials.length})</p>
          {partials.map(p => (
            <div key={p.id} className="flex items-center gap-2 text-xs font-sans py-1.5 px-2 rounded-sm bg-white/[0.02] border border-white/[0.04]" data-testid={`partial-row-${p.id}`}>
              <span className="uppercase tracking-[0.1em] text-[10px] font-bold w-12 text-zinc-400">{p.method}</span>
              <span className="flex-1 text-white font-mono">${Number(p.amount).toFixed(2)}</span>
              <span className="text-zinc-500 text-[10px]">{p.recorded_by_name || ''}</span>
              {canVoid && (
                <button
                  onClick={() => voidPartial(p.id)}
                  disabled={working}
                  className="text-red-400 hover:text-red-300 p-1 disabled:opacity-50"
                  title="Void partial"
                  data-testid={`void-partial-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Finalize button — only when remaining is settled */}
      {summary.fully_settled && (
        <Button
          onClick={finalize}
          disabled={working}
          className="w-full h-11 bg-emerald-500/20 text-emerald-200 border border-emerald-500/60 hover:bg-emerald-500/30 font-bold tracking-[0.1em] uppercase text-sm rounded-sm font-sans disabled:opacity-50"
          data-testid="finalize-partial"
        >
          <Check className="w-4 h-4 mr-1.5" />
          {working ? 'Finalising…' : (targetType === 'tab' ? 'Close Tab' : 'Mark Order Paid')}
        </Button>
      )}
    </div>
  );
}
